/**
 * Talentpool: Bewerbungen von gefas-mbh.de (docs/talentpool-plan.md).
 *
 * Die Website (eigenes Supabase-Projekt) leitet jede neue Bewerbung signiert an
 * `POST /api/website-eingang` weiter; die Route legt sie in `public.talentpool`
 * (Nr. 188) ab und kopiert die Anhänge in die private Ablage
 * `talentpool-dateien`. Gelesen wird mit dem Recht `talentpool.verwalten`,
 * Status und Notiz ändert `talentpool_bearbeiten`, gelöscht wird über
 * `talentpool_loeschen` (beide Nr. 189).
 *
 * Diese Datei ist der Vertrag zwischen SQL, Route und Oberfläche: Spaltennamen,
 * Grenzen und Wertelisten stehen hier und zeichengleich in Nr. 188.
 */

/** Rollen der Website (Check `bewerbungen_rolle_check` dort). Unbekannte Werte kommen als Text durch. */
export type TalentRolle = 'sigeko' | 'hse' | 'sifa' | 'mehrere';
export const TALENT_ROLLEN: readonly TalentRolle[] = ['sigeko', 'hse', 'sifa', 'mehrere'];

/** Arbeitsformen der Website. Eine Bewerbung kann mehrere tragen. */
export type TalentArbeitsform = 'fest' | 'frei' | 'teilzeit';
export const TALENT_ARBEITSFORMEN: readonly TalentArbeitsform[] = ['fest', 'frei', 'teilzeit'];

/**
 * Bearbeitungsstand im Talentpool. `zusage` und `absage` schließen das
 * Verfahren ab (`abgeschlossen_am`); ohne Talentpool-Einwilligung beginnt
 * damit die Löschfrist von sechs Monaten.
 */
export type TalentStatus = 'neu' | 'kontaktiert' | 'gespraech' | 'pool' | 'zusage' | 'absage';
export const TALENT_STATUS: readonly TalentStatus[] = ['neu', 'kontaktiert', 'gespraech', 'pool', 'zusage', 'absage'];
export const TALENT_STATUS_ABGESCHLOSSEN: readonly TalentStatus[] = ['zusage', 'absage'];

/** Höchstlängen in Zeichen — zeichengleich als CHECK in Nr. 188. Die Route kürzt, statt abzulehnen. */
export const TALENT_GRENZEN = {
    rolle: 30,
    arbeitsform_eintrag: 30,
    arbeitsform_anzahl: 10,
    qualifikation_eintrag: 200,
    qualifikation_anzahl: 20,
    qualifikation_sonstiges: 1000,
    abschluss: 200,
    plz_ort: 120,
    ort_naehe: 120,
    name: 200,
    email: 254,
    telefon: 60,
    nachricht: 5000,
    herkunft: 120,
    quelle_seite: 500,
    kampagne: 120,
    notiz: 5000,
    datei_name: 200,
} as const;

/** Erfahrung in Jahren: 0 bis 60 (wie die Website). Umkreis in km: 1 bis 1000. */
export const TALENT_ERFAHRUNG_MAX = 60;
export const TALENT_UMKREIS_MAX = 1000;

/** Ein Anhang in der Ablage `talentpool-dateien`. `nr` ist die Position in der Lieferung (1, 2, 3). */
export interface TalentDatei {
    nr: number;
    /** `<company_id>/<talentpool_id>/<nr>-<sicherer Name>` */
    pfad: string;
    name: string;
    typ: string;
    groesse: number;
}

/** Eine Zeile von `public.talentpool`, wie der Client sie liest (ohne `daten_roh`). */
export interface TalentpoolZeile {
    id: string;
    company_id: string;
    website_id: string;
    eingegangen_am: string;
    created_at: string;
    updated_at: string;
    rolle: string;
    arbeitsform: string[];
    lat: number | null;
    lng: number | null;
    plz_ort: string | null;
    ort_naehe: string | null;
    /** Umkreis laut Bewerbung; null = keine Angabe oder bundesweit (siehe `bundesweit`). */
    umkreis_km: number | null;
    bundesweit: boolean;
    qualifikation: string[];
    qualifikation_sonstiges: string | null;
    abschluss: string | null;
    erfahrung_jahre: number | null;
    /** YYYY-MM-DD */
    verfuegbar_ab: string | null;
    name: string;
    email: string | null;
    telefon: string | null;
    nachricht: string | null;
    herkunft: string | null;
    quelle_seite: string | null;
    /** Aus `angaben.kampagne`, z. B. „Partnernetzwerk“. */
    kampagne: string | null;
    /** Formularhaken „bis zu zwei Jahre im Talentpool speichern“ (`daten.talentpool`). */
    einwilligung_talentpool: boolean;
    /** Datenschutz-Einwilligung des Formulars (`daten.einwilligung`). */
    einwilligung_datenschutz: boolean;
    /** Alle Zusatzangaben der Website (Umkreis als Text, Tage pro Woche, Ausbildung …). */
    angaben: Record<string, unknown>;
    dateien: TalentDatei[];
    /** Anzahl der Anhänge, die die Website angekündigt hat; mehr als `dateien.length` = noch nicht alle da. */
    dateien_erwartet: number;
    status: TalentStatus;
    notiz: string | null;
    bearbeitet_von: string | null;
    bearbeitet_am: string | null;
    abgeschlossen_am: string | null;
    /** YYYY-MM-DD, vom Trigger berechnet; null = Frist läuft noch nicht. */
    loeschen_ab: string | null;
}

/** Spalten, die der Client liest (`select`), ohne `daten_roh`. */
export const TALENTPOOL_SPALTEN = [
    'id', 'company_id', 'website_id', 'eingegangen_am', 'created_at', 'updated_at',
    'rolle', 'arbeitsform', 'lat', 'lng', 'plz_ort', 'ort_naehe', 'umkreis_km', 'bundesweit',
    'qualifikation', 'qualifikation_sonstiges', 'abschluss', 'erfahrung_jahre', 'verfuegbar_ab',
    'name', 'email', 'telefon', 'nachricht', 'herkunft', 'quelle_seite', 'kampagne',
    'einwilligung_talentpool', 'einwilligung_datenschutz', 'angaben', 'dateien', 'dateien_erwartet',
    'status', 'notiz', 'bearbeitet_von', 'bearbeitet_am', 'abgeschlossen_am', 'loeschen_ab',
] as const;

// ─── Eingang von der Website ─────────────────────────────────────────────────

export const TALENTPOOL_ABLAGE = 'talentpool-dateien';

/** Nur Anhänge aus der Bewerbungsablage der Website (Schutz gegen SSRF). */
export const WEBSITE_DATEI_PRAEFIX = 'https://yjyeulayymuuepzddnqw.supabase.co/storage/v1/object/sign/bewerbungen-dateien/';
export const WEBSITE_DATEIEN_MAX = 3;
export const WEBSITE_DATEI_MAX_BYTES = 10 * 1024 * 1024;
export const WEBSITE_DATEI_TYPEN = ['application/pdf', 'image/jpeg', 'image/png'] as const;

/** Zeitfenster der Signatur in Sekunden (±). Die Website signiert jeden Versuch neu. */
export const WEBSITE_SIGNATUR_FENSTER_S = 300;

/** Mandant, falls `WEBSITE_EINGANG_FIRMA` in Vercel fehlt: GefAS. */
export const WEBSITE_EINGANG_FIRMA_STANDARD = '24632';

export interface WebsiteEingangDatei {
    name: string;
    url: string;
    typ: string;
}

/** Body von `POST /api/website-eingang`. */
export interface WebsiteEingang {
    art: 'anfrage' | 'bewerbung';
    website_id: string;
    erstellt_am: string;
    daten: Record<string, unknown>;
    dateien: WebsiteEingangDatei[];
}

export type WebsiteEingangFehler = 'eingabe' | 'signatur' | 'zeit' | 'art' | 'datei' | 'server';

export type WebsiteEingangAntwort =
    | { ok: true; id: string; neu: boolean }
    | { ok: false; fehler: WebsiteEingangFehler; text?: string; id?: string };
